import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import TopNavView from './top-nav-view';
import useTopNavData from './use-top-nav-data';
import { emitPhoneCallClickEvent, emitNavClick } from '../../utils/gtm/events';

const TopNavContainer = () => {
  const { pathname } = useLocation();
  const { nodes } = useTopNavData();
  const isTravelCompany = false;
  const menuNames = useMemo(
    () => (Array.isArray(nodes) ? nodes.map((tab) => tab.name) : []),
    [nodes],
  );
  const [activeMenu, setActiveMenu] = useState<string | undefined>();
  const [activeSubMenu, setActiveSubMenu] = useState<string | undefined>();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const wasNavOpenRef = useRef(false);
  const activeMenuRef = useRef<HTMLButtonElement>(null);
  const firstSubMenuRef = useRef<Record<string, HTMLElement | null>>({});
  const useSlimNav = pathname.includes('trip-matches');
  const isNavOpen = Boolean(showMobileMenu || activeMenu);

  const onCloseMenu = useCallback((closeMobileMenu = true) => {
    setActiveMenu(undefined);
    setActiveSubMenu(undefined);
    if (closeMobileMenu) {
      setShowMobileMenu(false);
    }
  }, []);

  const onClickLink = useCallback(() => {
    onCloseMenu();
    setSavedScrollPosition(0);
    window.scrollTo(0, 0);
  }, [onCloseMenu]);

  const onReturnToMainMenu = useCallback(() => {
    if (activeMenu && activeMenuRef.current) {
      activeMenuRef.current.focus();
    }
  }, [activeMenu]);

  useEffect(() => {
    if (isNavOpen) {
      // Apply saved scroll position to CSS
      document.documentElement.style.setProperty('--scroll-position', `-${savedScrollPosition}px`);
      wasNavOpenRef.current = true;
      return;
    }

    document.documentElement.style.removeProperty('--scroll-position');

    // Only restore scroll when closing the nav. Otherwise this fires on every
    // navigation and overrides route-level scroll restoration.
    if (!wasNavOpenRef.current) {
      return;
    }
    wasNavOpenRef.current = false;

    // Use requestAnimationFrame to ensure the CSS transition has completed
    // This is especially important on Android devices
    requestAnimationFrame(() => {
      // Try multiple methods to ensure scroll restoration works on all devices
      window.scrollTo(0, savedScrollPosition);

      // Fallback for Android: also try setting scrollTop directly
      if (document.documentElement.scrollTop !== savedScrollPosition) {
        document.documentElement.scrollTop = savedScrollPosition;
      }
      if (document.body.scrollTop !== savedScrollPosition) {
        document.body.scrollTop = savedScrollPosition;
      }
    });
  }, [isNavOpen, savedScrollPosition]);

  useEffect(() => {
    const handleResize = () => {
      if (showMobileMenu && !activeMenu && window.innerWidth >= 900) {
        setShowMobileMenu(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [showMobileMenu, activeMenu]);

  // Add global keyboard event listener for escape key
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (activeMenu || showMobileMenu)) {
        onCloseMenu();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [activeMenu, showMobileMenu, onCloseMenu]);

  const onMenuInitialOpen = () => {
    // Set the scrollbar width to the width of the scrollbar to avoid shifting
    // content when the menu is opened for users who have a mouse
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    // Apply saved scroll position to CSS and set the scroll position to the saved position
    // This is used to preserve the scroll position when the menu is closed without navigating to a new page
    document.documentElement.style.setProperty('--scroll-position', `-${savedScrollPosition}px`);
    setSavedScrollPosition(window.scrollY || document.documentElement.scrollTop);
  };

  const onOpenMobileMenu = () => {
    onMenuInitialOpen();
    setShowMobileMenu(true);
  };

  const onSelectMenu = (menuName: string | undefined) => {
    if (!isNavOpen) {
      onMenuInitialOpen();
    }
    setActiveMenu(menuName);
    const menu = Array.isArray(nodes) ? nodes.find((node) => node.name === menuName) : null;
    if (menu && window.innerWidth >= 900) {
      setActiveSubMenu(menu.linkSections[0].name);
      // Focus the first submenu item for the active menu
      if (menuName) {
        firstSubMenuRef.current[menuName]?.focus();
      }
    }
  };

  return (
    <TopNavView
      activeMenu={activeMenu}
      activeMenuRef={activeMenuRef}
      activeSubMenu={activeSubMenu}
      emitPhoneCallClickEvent={emitPhoneCallClickEvent}
      emitNavClick={emitNavClick}
      firstSubMenuRef={firstSubMenuRef}
      isTravelCompany={isTravelCompany}
      menuNames={menuNames}
      menus={Array.isArray(nodes) ? nodes : []}
      onClickLink={onClickLink}
      onCloseMenu={onCloseMenu}
      onOpenMobileMenu={onOpenMobileMenu}
      onReturnToMainMenu={onReturnToMainMenu}
      onSelectMenu={onSelectMenu}
      setActiveSubMenu={setActiveSubMenu}
      showMobileMenu={showMobileMenu}
      useSlimNav={useSlimNav}
    />
  );
};
export default TopNavContainer;
